import { useMemo } from "react";
import { SortableContext } from "@dnd-kit/sortable";
import type { BlockRendererMapping } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { DroppableArea } from "@circle-react-shared/BuilderUI/DragAndDrop/DroppableArea";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import type { BlockCategoryName } from "@circle-react-shared/BuilderUI/constants/BlockCategories";
import { DroppablePreview } from "./DroppablePreview";
import { Preview } from "./Preview";
import { SortablePreview } from "./SortablePreview";

export interface PreviewBlockListProps {
  accepts: BlockCategoryName[];
  blocks: BaseBlock[];
  components: BlockRendererMapping;
  path: string;
  rejects: BlockCategoryName[];
  shouldShowBlockHover: boolean;
}

export const PreviewBlockList = ({
  blocks,
  components,
  path,
  accepts,
  rejects,
  shouldShowBlockHover,
}: PreviewBlockListProps) => {
  const dragAndDropType = useBuilderStore(state => state.dragAndDropType);

  // Memoize expensive path computation
  const childrenArrayPath = useMemo(
    () => path.replace(/^blocks\./, "").replace(/\.value+$/, ""),
    [path],
  );

  // Memoize block IDs for SortableContext
  const blockIds = useMemo(() => blocks.map(block => block.id), [blocks]);

  // Memoize JSON serialization of accepts/rejects
  const acceptsJson = useMemo(() => JSON.stringify(accepts), [accepts]);
  const rejectsJson = useMemo(() => JSON.stringify(rejects), [rejects]);

  switch (dragAndDropType) {
    case "sortable-elements":
      // TODO: This implementation is still not working as expected.
      return (
        <SortableContext items={blockIds}>
          {blocks.map((block, index) => (
            <SortablePreview
              key={block.id}
              block={block}
              components={components}
              path={`${path}.${index}`}
              shouldShowBlockHover={shouldShowBlockHover}
            />
          ))}
        </SortableContext>
      );
    case "floating-dropzones":
      // NOTE: This is a "ghost" container. The `display: contents` style ensures
      // that this div does not generate a box in the layout, so it doesn't
      // interfere with parent flex or grid layouts. However, it still exists
      // in the DOM, allowing us to use it as a reliable measurement anchor.
      // https://caniuse.com/css-display-contents
      return (
        <div
          style={{ display: "contents" }}
          data-block-list-container="true"
          data-parent-path={childrenArrayPath}
          data-accepts={acceptsJson}
          data-rejects={rejectsJson}
        >
          {blocks.map((block, index) => (
            <Preview
              key={block.id}
              block={block}
              components={components}
              path={`${path}.${index}`}
              data-measurable-block="true"
              data-parent-path={childrenArrayPath}
              data-block-index={index}
              data-accepts={acceptsJson}
              data-rejects={rejectsJson}
              shouldShowBlockHover={shouldShowBlockHover}
            />
          ))}
        </div>
      );
    case "elements-as-dropzones":
      return (
        <>
          {blocks.length === 0 && (
            <DroppableArea
              path={childrenArrayPath}
              accepts={accepts}
              rejects={rejects}
              index={blocks.length}
            />
          )}
          {blocks.map((block, index) => (
            <DroppablePreview
              key={block.id}
              block={block}
              components={components}
              path={`${path}.${index}`}
              childrenArrayPath={childrenArrayPath}
              index={index}
              shouldShowBlockHover={shouldShowBlockHover}
              accepts={accepts}
              rejects={rejects}
            />
          ))}
        </>
      );
    case "dropzone-at-bottom":
      return (
        <>
          {blocks.map((block, index) => (
            <Preview
              key={block.id}
              block={block}
              components={components}
              path={`${path}.${index}`}
              shouldShowBlockHover={shouldShowBlockHover}
            />
          ))}
          <DroppableArea
            path={childrenArrayPath}
            accepts={accepts}
            rejects={rejects}
            index={blocks.length}
          />
        </>
      );
    case "disabled":
    default:
      return (
        <>
          {blocks.map((block, index) => (
            <Preview
              key={block.id}
              block={block}
              components={components}
              path={`${path}.${index}`}
              shouldShowBlockHover={shouldShowBlockHover}
            />
          ))}
        </>
      );
  }
};
